import type {
  BdHierarchicalLabel,
  BdHierarchicalValueNode,
} from '@bindoc-projects/chart-domain-lib';
import {
  BD_CHART_TOOLTIP_DEFAULT,
  BD_NUMERIC_CHART_AXIS_OPTIONS_DEFAULT
} from '@bindoc-projects/chart-domain-lib';
import { BdNumberFormatter } from '@bindoc-projects/util-domain-lib';
import type { ChartConfiguration, ChartDataset, TooltipItem } from 'chart.js';
import type { Options } from 'chartjs-plugin-datalabels/types/options';
import type { HtmCodeGroupId } from '../code-group';
import type { HtmCaseCountTimeline } from './case-count-data';
import NumberFormat = Intl.NumberFormat;

/**
 * chart.js types `bar` data as `(number | [number, number])[]`. With a
 * hierarchical x-axis the values are `BdHierarchicalValueNode` objects instead — a
 * shape only chartjs-plugin-hierarchical's type augmentation describes, and
 * importing that here would put a rendering package back on the eager path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HtmCaseCountBarDataset = ChartDataset<'bar', any> & {
  datalabels?: Options;
};

export const HTM_OPPORTUNITY_COSTS_CASE_COUNT_CHART_FULLY_INPATIENT_CASES_AXIS_ID =
  'fullyInpatientCasesCount';
export const HTM_OPPORTUNITY_COSTS_CASE_COUNT_CHART_FULLY_INPATIENT_CASES_CHANGE_RATE_AXIS_ID =
  'fullyInpatientCasesChaneRate';

export const HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_CALCULATED_COLOR =
  '#1a5e7e';
export const HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_IST_COLOR =
  '#2889b8';

export class HtmOpportunityCostsCaseCountOperatingNumber {
  public static transformDataFromValueToObject(data: {
    [year: string]: number;
  }): HtmCaseCountTimeline {
    const transformedData: HtmCaseCountTimeline = {};

    Object.keys(data).forEach((year) => {
      transformedData[year] = {
        caseCount: data[year],
        calculated: 'BERECHNET',
      };
    });

    return transformedData;
  }

  public static sum(
    data: Map<HtmCodeGroupId, HtmCaseCountTimeline>,
  ): HtmOpportunityCostsCaseCountOperatingNumber {
    const dataSum: HtmCaseCountTimeline = {};

    data.forEach((value) => {
      Object.keys(value).forEach((year) => {
        if (dataSum[year]) {
          dataSum[year].caseCount += value[year].caseCount;

          if (
            dataSum[year].calculated === 'IST' &&
            value[year].calculated === 'BERECHNET'
          ) {
            dataSum[year].calculated = 'BERECHNET';
          }
        } else {
          dataSum[year] = {
            caseCount: value[year].caseCount,
            calculated: value[year].calculated,
          };
        }
      });
    });

    return new HtmOpportunityCostsCaseCountOperatingNumber(dataSum);
  }

  /**
   * Just a hack because (useless) Map is used above
   * @param data
   */
  public static sumRaw(data: {
    [id in HtmCodeGroupId]?: {
      [year: string]: {
        caseCount: number;
        calculated: 'BERECHNET' | 'IST';
      };
    };
  }): HtmOpportunityCostsCaseCountOperatingNumber {
    const dataSum: {
      [year: string]: { caseCount: number; calculated: 'BERECHNET' | 'IST' };
    } = {};

    Object.values(data).forEach((value) => {
      Object.keys(value).forEach((year) => {
        if (dataSum[year]) {
          dataSum[year].caseCount += value[year].caseCount;

          if (
            dataSum[year].calculated === 'IST' &&
            value[year].calculated === 'BERECHNET'
          ) {
            dataSum[year].calculated = 'BERECHNET';
          }
        } else {
          dataSum[year] = {
            caseCount: value[year].caseCount,
            calculated: value[year].calculated,
          };
        }
      });
    });

    console.log(dataSum);

    return new HtmOpportunityCostsCaseCountOperatingNumber(dataSum);
  }

  constructor(
    public data: HtmCaseCountTimeline,
    public chartName: string = null,
    public operatingNumberDescription: string = null,
    public ssiGrouping: {
      [ssi: string]: { timeLine: HtmCaseCountTimeline; ssiDisplayName: string };
    } = null,
  ) {}

  private _getChartDataSets(withChangeRate: boolean): ChartDataset<'bar'>[] {
    return [this._getChartDataSetFullyInpatientCases(withChangeRate)];
  }

  private _getChartDataSetFullyInpatientCases(
    withChangeRate: boolean,
  ): HtmCaseCountBarDataset {
    const numberFormatter: NumberFormat = new Intl.NumberFormat('de', {
      maximumFractionDigits: 0,
    });
    const numberFormatterChangeRate: NumberFormat = new Intl.NumberFormat(
      'de',
      {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
    );

    let datalabels: Options = {
      labels: {
        current: {
          display: true,
          formatter: (value) => numberFormatter.format(value),
          font: {
            family: 'Montserrat',
            size: 14,
            weight: 'bold',
          },
          borderRadius: 2,
          color: 'rgba(0,0,0,0.87)',
          anchor: 'end',
          align: 'top',
          offset: 16,
        },
      },
    };

    // if the change rate should be shown
    // the datalabels feature of multiple labels will be used:
    // https://chartjs-plugin-datalabels.netlify.app/guide/labels.html#multiple-labels
    if (withChangeRate) {
      datalabels = {
        labels: {
          title: {
            display: true,
            formatter: (value) => numberFormatter.format(value),
            font: {
              family: 'Montserrat',
              size: 14,
              weight: 'bold',
            },
            borderRadius: 2,
            color: 'rgba(0,0,0,0.87)',
            anchor: 'end',
            align: 'top',
            offset: 16,
          },
          value: {
            formatter: (value, context) => {
              const valueFirst: number = context.dataset.data[
                context.dataIndex - 1
              ] as number;
              const valueSecond: number = context.dataset.data[
                context.dataIndex
              ] as number;

              if (valueFirst) {
                const changeRate = (valueSecond / valueFirst - 1) * 100;
                return `${numberFormatterChangeRate.format(changeRate)} %`;
              } else {
                return null;
              }
            },
            font: {
              family: 'Montserrat',
              size: 10,
              weight: 'bold',
            },
            borderRadius: 2,
            color: (context) => {
              const valueFirst: number = context.dataset.data[
                context.dataIndex - 1
              ] as number;
              const valueSecond: number = context.dataset.data[
                context.dataIndex
              ] as number;

              if (valueFirst === valueSecond) {
                return 'rgba(0,0,0,0.87)';
              }

              if (valueFirst > valueSecond) {
                return 'red';
              } else {
                return 'green';
              }
            },
            anchor: 'end',
            align: 'top',
            offset: 4,
          },
        },
      };
    }

    let data: number[] | BdHierarchicalValueNode<number>[] = Object.values(
      this.data,
    ).map((it) => it.caseCount);
    if (this.ssiGrouping) {
      data = Object.keys(this.data).map((year) => {
        const value = this.data[year];
        if (value.calculated === 'IST') {
          const children: BdHierarchicalValueNode<number>[] = Object.values(
            this.ssiGrouping,
          )
            .map((it) => it.timeLine[year])
            .filter((caseCountData) => !!caseCountData)
            .map((caseCountData) => {
              const result: BdHierarchicalValueNode<number> =
                caseCountData.caseCount;
              return result;
            });

          return <BdHierarchicalValueNode<number>>{
            value: value.caseCount,
            children: children,
          };
        } else {
          return value.caseCount;
        }
      });
    }

    return {
      data,
      label: this.chartName,
      barPercentage: 0.6,
      order: 2,
      backgroundColor: Object.values(this.data).map((it) => {
        return it.calculated === 'BERECHNET'
          ? HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_CALCULATED_COLOR
          : HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_IST_COLOR;
      }),
      datalabels,
    };
  }

  public getChartConfiguration(
    withChangeRate: boolean,
    numberFormatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  ): ChartConfiguration<'bar'> {
    let labels: string[] | BdHierarchicalLabel[] = Object.keys(this.data);

    if (this.ssiGrouping) {
      labels = Object.keys(this.data).map((year) => {
        if (this.data[year].calculated === 'IST') {
          return <BdHierarchicalLabel>{
            label: year,
            backgroundColor:
              HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_IST_COLOR,
            children: Object.values(this.ssiGrouping)
              .filter((it) => !!it.timeLine[year])
              .map((it) => ({
                label: it.ssiDisplayName,
                backgroundColor:
                  HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_IST_COLOR,
              })),
          };
        } else {
          return <BdHierarchicalLabel>{
            label: year,
            backgroundColor:
              HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_CALCULATED_COLOR,
          };
        }
      });
    }

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: {
        datasets: this._getChartDataSets(withChangeRate),
        labels,
      },
      options: {
        maintainAspectRatio: false,
        scales: {
          y: {
            ...BD_NUMERIC_CHART_AXIS_OPTIONS_DEFAULT,
            beginAtZero: true,
            id: HTM_OPPORTUNITY_COSTS_CASE_COUNT_CHART_FULLY_INPATIENT_CASES_AXIS_ID,
            gridLines: {
              drawBorder: false,
            },
            scaleLabel: {
              display: !!this.chartName,
              labelString: this.chartName,
            },
          },
          x: {
            type: this.ssiGrouping ? ('hierarchical' as any) : 'category',
          },
        },
        layout: {
          padding: {
            top: 40,
            // add more space at the bottom for the hierarchy
            bottom: 60,
          },
        },
        plugins: {
          tooltip: {
            ...(BD_CHART_TOOLTIP_DEFAULT as object),
            callbacks: {
              label(tooltipItem: TooltipItem<'bar'>): string | string[] {
                return BdNumberFormatter.format(
                  tooltipItem.raw as number,
                  numberFormatOptions,
                );
              },
            },
          },
          legend: {
            display: false,
          },
        },
      },
    };

    if (this.ssiGrouping) {
      config.options.scales['x'] = {
        type: 'hierarchical' as any,
        attributes: {
          backgroundColor:
            HTM_OPPORTUNITY_COSTS_CASE_COUNT_OPERATING_NUMBER_IST_COLOR,
        },
      } as any;
    }

    return config;
  }
}
